import { ErrorDisplay } from "@bandwith/ui";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { DetailsLayout } from "./components/layouts/DetailsLayout";
import { MainLayout } from "./components/layouts/MainLayout";
import { StandaloneLayout } from "./components/layouts/StandaloneLayout";
import {
  setlistFeedbackQuery,
  setlistQuery,
  setlistsQuery,
  songFeedbackQuery,
  songQuery,
  songsQuery,
} from "./lib/queries";
import { queryClient } from "./lib/queryClient";
import { About } from "./routes/About";
import { AdminUsers } from "./routes/AdminUsers";
import { Home } from "./routes/Home";
import { LinkPreview } from "./routes/LinkPreview";
import { SetlistDetail } from "./routes/SetlistDetail";
import { SetlistEntry } from "./routes/SetlistEntry";
import { SetlistNew } from "./routes/SetlistNew";
import { Setlists } from "./routes/Setlists";
import { SignIn } from "./routes/SignIn";
import { SongDetail } from "./routes/SongDetail";
import { SongNew } from "./routes/SongNew";
import { SongNotes } from "./routes/SongNotes";

function layoutError(context: string) {
  return function LayoutError({ error, reset }: ErrorComponentProps) {
    return <ErrorDisplay error={error} reset={reset} context={context} />;
  };
}

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()();

const mainLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "main",
  component: MainLayout,
  errorComponent: layoutError("Main layout"),
});

const detailsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "details",
  component: DetailsLayout,
  errorComponent: layoutError("Details layout"),
});

const standaloneLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "standalone",
  component: StandaloneLayout,
  errorComponent: layoutError("Standalone layout"),
});

// Loaders prefetch into the query cache without awaiting, so data loads
// in parallel with rendering and components keep their own loading states.
const indexRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: "/",
  component: Home,
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(songsQuery());
  },
});

const aboutRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: "/about",
  component: About,
});

const setlistsRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: "/setlists",
  component: Setlists,
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(setlistsQuery());
  },
});

const adminUsersRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: "/admin/users",
  component: AdminUsers,
});

type LinkPreviewSearch = {
  path?: string;
};

export const linkPreviewRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: "/link-preview",
  component: LinkPreview,
  validateSearch: (search: Record<string, unknown>): LinkPreviewSearch => {
    const path = search.path;
    // Require a single leading slash: "//host/x" is protocol-relative
    // and would let a crafted link fire a cross-origin fetch.
    if (typeof path === "string" && /^\/(?!\/)/.test(path)) {
      return { path };
    }
    return {};
  },
});

type SongDetailSearch = {
  action?: "record" | "upload" | "edit";
  from?: string;
};

export const songDetailRoute = createRoute({
  getParentRoute: () => detailsLayoutRoute,
  path: "/songs/$id",
  component: SongDetail,
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(songQuery(params.id));
    void context.queryClient.prefetchQuery(songFeedbackQuery(params.id));
  },
  validateSearch: (search: Record<string, unknown>): SongDetailSearch => {
    const result: SongDetailSearch = {};
    const action = search.action;
    if (action === "record" || action === "upload" || action === "edit") {
      result.action = action;
    }
    const from = search.from;
    if (typeof from === "string") {
      result.from = from;
    }
    return result;
  },
});

export const songNewRoute = createRoute({
  getParentRoute: () => detailsLayoutRoute,
  path: "/songs/new",
  component: SongNew,
  validateSearch: (search: Record<string, unknown>): SongDetailSearch => {
    const action = search.action;
    if (action === "record" || action === "upload") {
      return { action };
    }
    return {};
  },
});

type SetlistDetailSearch = {
  action?: "edit";
};

export const setlistDetailRoute = createRoute({
  getParentRoute: () => detailsLayoutRoute,
  path: "/setlists/$id",
  component: SetlistDetail,
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(setlistQuery(params.id));
    void context.queryClient.prefetchQuery(setlistFeedbackQuery(params.id));
  },
  validateSearch: (search: Record<string, unknown>): SetlistDetailSearch => {
    const action = search.action;
    if (action === "edit") {
      return { action };
    }
    return {};
  },
});

/** A title-only setlist entry's page, so setlist paging stops on it */
export const setlistEntryRoute = createRoute({
  getParentRoute: () => detailsLayoutRoute,
  path: "/setlists/$id/entries/$entryId",
  component: SetlistEntry,
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(setlistQuery(params.id));
  },
});

export const setlistNewRoute = createRoute({
  getParentRoute: () => detailsLayoutRoute,
  path: "/setlists/new",
  component: SetlistNew,
});

export const signInRoute = createRoute({
  getParentRoute: () => detailsLayoutRoute,
  path: "/auth/signin",
  component: SignIn,
});

type SongNotesSearch = {
  from?: string;
};

export const songNotesRoute = createRoute({
  getParentRoute: () => standaloneLayoutRoute,
  path: "/songs/$id/notes",
  component: SongNotes,
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(songQuery(params.id));
  },
  validateSearch: (search: Record<string, unknown>): SongNotesSearch => {
    const from = search.from;
    if (typeof from === "string") {
      return { from };
    }
    return {};
  },
});

const routeTree = rootRoute.addChildren([
  mainLayoutRoute.addChildren([
    indexRoute,
    aboutRoute,
    setlistsRoute,
    adminUsersRoute,
    linkPreviewRoute,
  ]),
  detailsLayoutRoute.addChildren([
    songDetailRoute,
    songNewRoute,
    setlistDetailRoute,
    setlistEntryRoute,
    setlistNewRoute,
    signInRoute,
  ]),
  standaloneLayoutRoute.addChildren([songNotesRoute]),
]);

export const router = createRouter({ routeTree, context: { queryClient } });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
